/**
 * MentionTextarea - composant universel pour saisir du texte avec @mentions equipe.
 * Reutilisable partout (notes projet, commentaires, qualification, delais, etc).
 *
 * La liste des @handles est chargee dynamiquement via useTeamHandles (toute
 * personne presente dans /users/mentionable est taguable, y compris Tony/Charles
 * et tout nouveau collaborateur).
 */
import React, { useState, useRef } from 'react';
import { useTeamHandles, useKnownMentionSet } from '../hooks/useTeamHandles';

export const renderTextWithMentions = (text, knownSet) => {
  if (!text) return text;
  const parts = text.split(/(@[a-zA-Z]+)/g);
  return parts.map((p, i) => {
    if (p.startsWith('@')) {
      const handle = p.slice(1).toLowerCase();
      if (!knownSet || knownSet.has(handle)) {
        return (
          <span key={i} className="bg-[#008A8D]/15 text-[#006B6E] px-1 py-0.5 rounded font-semibold">
            {p}
          </span>
        );
      }
    }
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
};

/**
 * Hook React qui retourne (text) => JSX, avec coloration des mentions connues.
 * Usage : const renderer = useMentionRenderer(); renderer(text);
 */
export const useMentionRenderer = () => {
  const known = useKnownMentionSet();
  return (text) => renderTextWithMentions(text, known);
};

export const MentionTextarea = ({
  value, onChange,
  placeholder = "Tapez @ pour mentionner un coequipier",
  rows = 3,
  className = "",
  testid = "mention-textarea",
}) => {
  const handles = useTeamHandles();
  const [showMenu, setShowMenu] = useState(false);
  const [filter, setFilter] = useState('');
  const taRef = useRef(null);

  const handleChange = (e) => {
    const v = e.target.value;
    onChange(v);
    const cursor = e.target.selectionStart;
    const before = v.slice(0, cursor);
    const m = before.match(/@([a-zA-Z]*)$/);
    if (m) {
      setShowMenu(true);
      setFilter(m[1].toLowerCase());
    } else {
      setShowMenu(false);
    }
  };

  const pick = (handle) => {
    if (!taRef.current) return;
    const ta = taRef.current;
    const cursor = ta.selectionStart;
    const before = value.slice(0, cursor).replace(/@[a-zA-Z]*$/, `@${handle} `);
    const after = value.slice(cursor);
    onChange(before + after);
    setShowMenu(false);
    setTimeout(() => {
      ta.focus();
      ta.setSelectionRange(before.length, before.length);
    }, 10);
  };

  const filtered = handles.filter(h => h.handle.startsWith(filter));

  return (
    <div className="relative">
      <textarea
        ref={taRef}
        value={value}
        onChange={handleChange}
        rows={rows}
        placeholder={placeholder}
        className={`w-full border border-gray-200 rounded-lg p-2 text-sm resize-none focus:border-[#008A8D] focus:outline-none ${className}`}
        data-testid={testid}
      />
      {showMenu && filtered.length > 0 && (
        <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-48 overflow-auto" data-testid={`${testid}-menu`}>
          {filtered.map(h => (
            <button
              key={h.handle}
              type="button"
              onClick={() => pick(h.handle)}
              className="w-full text-left px-3 py-2 text-sm hover:bg-[#008A8D]/10 flex items-center gap-2"
              data-testid={`${testid}-pick-${h.handle}`}>
              <span className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ background: h.color || '#13736D' }}>
                {h.handle.charAt(0).toUpperCase()}
              </span>
              <span><b>@{h.handle}</b> <span className="text-gray-400 text-xs">- {h.name}</span></span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default MentionTextarea;
